import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Users, Search, Filter, RefreshCw, Loader, Mail, Phone, Calendar,
    Building2, User, ShoppingBag, Trash2, Ban, CheckCircle, Download,
    ChevronDown, ChevronUp, AlertTriangle, X, Tag, Save, CreditCard, Crown, Gift, Clock, UserCheck, UserX, ExternalLink
} from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';
// S5 가입 위주 외주 업체 등록(2026-10-27): 승인·거절은 서버 callable 1회(알림도 서버) · 자격 번호는 vendorDocs 메타(본인·관리자만)에서 읽음
import { adminApproveSignup, listVendorDocs, getVendorProfile, MF_KIND_LABEL, VENDOR_STATUS_TEXT } from '../services/outsourceMfApi';

// 런칭 프로모션 컷오프 (App.js와 동일)
const LAUNCH_PROMO_CUTOFF = new Date('2026-04-10T00:00:00');

// ── S5 가입 승인 보조 상수 ──
const MFDS_LOOKUP_URL = 'https://emedi.mfds.go.kr';   // 식약처 안심책방(공개 조회) — 번호는 URL 에 붙이지 않는다(법무 §3-2)
const HOLD_REASON_DEFAULT = '허가·면허 번호 확인 필요 — 서류를 올려 주세요';
// 거절 사유 선택지(법무 §6 ①). 단정·제3자 정보 문안 금지(§6 ③)
const REJECT_CHOICES = ['사업자 정보 확인 불가', '회원 유형 자격 확인 불가(개설등록·제조업 허가)', '중복 가입', '허위 정보 기재'];
const QUAL_TYPE_LABEL = { device_mfr: '의료기기 제조업 허가', dental_lab: '치과기공소 개설등록' };
const VENDOR_KINDS = ['metal', 'cnc', 'zirconia', 'jig', 'etc'];
const fmtTs = (v) => { const d = v && typeof v.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : (typeof v === 'string' || typeof v === 'number') ? new Date(v) : null); return d && !isNaN(d.getTime()) ? d.toLocaleDateString('ko-KR') : ''; };

const AdminUserManagement = ({ userInfo }) => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryUser, setCategoryUser] = useState(null);
    const [categorySelections, setCategorySelections] = useState([]);
    const [savingCategories, setSavingCategories] = useState(false);
    const [signupStats, setSignupStats] = useState({ total: 0, today: 0, week: 0 });
    // S5: 자격 번호 메타(vendorDocs/{uid}/files) — 펼침·승인 모달에서만 읽음. 화면 밖(콘솔·alert)으로 내보내지 않는다
    const [vendorMeta, setVendorMeta] = useState({});   // { [uid]: { docs:[], loading, error } }
    const [approvalModal, setApprovalModal] = useState(null);   // { user, decision:'approved'|'rejected', mode:'decide'|'vendorOnly' } — vendorOnly = 이미 승인된 회원의 외주 등록 보정(서버 decideTransition vendorOnly)
    const [vendorProf, setVendorProf] = useState({});   // { [uid]: { prof: null|object, loading, error } } — vendorProfiles/{uid} 현재 상태(보정 진입점 표시용)
    const [apv, setApv] = useState(null);   // 승인 모달 입력 { grant, kinds, hold, holdReason, gmpDate, cncApproved, rejectChoice, rejectText }
    const [apvMsg, setApvMsg] = useState('');
    const [copyMsg, setCopyMsg] = useState('');

    const OUTSOURCE_CATEGORIES = [
        { id: 'custom_milling', label: 'Custom Milling' },
        { id: '3d_printing', label: '3D Printing' },
        { id: 'design', label: 'Design' },
        { id: 'etc', label: '기타' },
    ];

    const getCategoryLabel = (id) => {
        const cat = OUTSOURCE_CATEGORIES.find(c => c.id === id);
        return cat ? cat.label : id;
    };

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);

            // 활성/체험 구독 데이터 로드
            const subsByUser = {};
            try {
                const subSnap = await getDocs(query(
                    collection(db, 'appSubscriptions'),
                    where('status', 'in', ['active', 'trial'])
                ));
                subSnap.docs.forEach(d => {
                    const sub = d.data();
                    if (!sub.userId) return;
                    const exp = sub.expiresAt;
                    const expDate = exp ? (exp.toDate ? exp.toDate() : new Date(exp)) : null;
                    // 만료된 건 제외
                    if (expDate && expDate < new Date()) return;
                    subsByUser[sub.userId] = {
                        plan: sub.plan || sub.planType || '',
                        status: sub.status,
                        expiresAt: expDate,
                        amount: sub.amount || sub.price || 0,
                        billingMethod: sub.billingMethod || sub.paymentMethod || '',
                        startedAt: sub.startedAt?.toDate ? sub.startedAt.toDate() : (sub.startedAt ? new Date(sub.startedAt) : null),
                    };
                });
            } catch (e) {
                console.warn('구독 데이터 로드 실패:', e);
            }

            const userList = snapshot.docs.map(docSnap => {
                const data = docSnap.data();
                let createdAt = null;
                if (data.createdAt) {
                    if (typeof data.createdAt.toDate === 'function') {
                        createdAt = data.createdAt.toDate();
                    } else if (typeof data.createdAt === 'string') {
                        createdAt = new Date(data.createdAt);
                    } else if (data.createdAt instanceof Date) {
                        createdAt = data.createdAt;
                    }
                }

                // 구독 상태 판정
                // 1) 본인 또는 회사(companyId)의 active 구독 존재 = 실결제 구독중
                const ownSub = subsByUser[docSnap.id];
                const companySub = data.companyId ? subsByUser[data.companyId] : null;
                const activeSub = ownSub || companySub;
                const hasRealSubscription = !!activeSub;

                // 2) 런칭 프로모션 (createdAt이 컷오프 이전 또는 누락)
                const isLaunchPromo = !createdAt || createdAt < LAUNCH_PROMO_CUTOFF;

                // 최종 유료 여부
                const isPaid = hasRealSubscription || isLaunchPromo || data.isAdmin === true || data.role === 'admin';

                return {
                    id: docSnap.id,
                    ...data,
                    createdAt,
                    _subscription: activeSub,
                    _hasRealSubscription: hasRealSubscription,
                    _isLaunchPromo: isLaunchPromo && !hasRealSubscription,
                    _isPaid: isPaid,
                };
            });

            setUsers(userList);
        } catch (error) {
            console.error('사용자 로드 실패:', error);
            alert('사용자 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }
        const isAdmin = isAdminUser(userInfo);
        if (!isAdmin) {
            alert('관리자만 접근 가능합니다.');
            navigate('/dashboard');
            return;
        }
        loadUsers();
        loadSignupStats();
    }, [userInfo, navigate, loadUsers]);

    const loadSignupStats = async () => {
        try {
            const now = new Date();
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

            const allSnap = await getDocs(collection(db, 'signupVisits'));
            const total = allSnap.size;

            let today = 0, week = 0;
            allSnap.docs.forEach(d => {
                const t = d.data().visitedAt;
                if (!t) return;
                const date = t.toDate ? t.toDate() : new Date(t);
                if (date >= todayStart) today++;
                if (date >= weekAgo) week++;
            });

            setSignupStats({ total, today, week });
        } catch (e) { console.log('signupStats error:', e); }
    };

    const getUserType = (user) => {
        if (user.isAdmin || user.role === 'admin') return 'admin';
        if (user.businessType === 'seller') return 'seller';
        if (user.businessType === 'dental' || user.companyBusinessType === 'dental') return 'dental';
        if (user.businessType === 'lab' || user.companyBusinessType === 'lab') return 'lab';
        if (user.businessType === 'outsource' || user.companyBusinessType === 'outsource') return 'outsource';
        if (user.businessType === 'marketer') return 'marketer';
        if (user.userType === 'individual') return 'individual';
        if (user.companyId) return 'staff';
        return 'unknown';
    };

    const getUserTypeLabel = (type) => {
        const labels = {
            admin: '관리자',
            seller: '판매자',
            dental: '치과',
            lab: '기공소',
            outsource: '외주업체',
            marketer: '마케터',
            individual: '개인',
            staff: '직원',
            unknown: '미분류',
        };
        return labels[type] || type;
    };

    const getUserTypeColor = (type) => {
        const colors = {
            admin: { bg: '#fef3c7', text: '#92400e' },
            seller: { bg: '#dbeafe', text: '#1e40af' },
            dental: { bg: '#d1fae5', text: '#065f46' },
            lab: { bg: '#e0e7ff', text: '#3730a3' },
            outsource: { bg: '#ede9fe', text: '#6d28d9' },
            marketer: { bg: '#fce7f3', text: '#9d174d' },
            individual: { bg: '#f3f4f6', text: '#374151' },
            staff: { bg: '#e5e7eb', text: '#4b5563' },
            unknown: { bg: '#f1f5f9', text: '#64748b' },
        };
        return colors[type] || colors.unknown;
    };

    const filteredUsers = users.filter(user => {
        const userType = getUserType(user);
        const matchesSearch = !searchQuery ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.businessName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.phone?.includes(searchQuery);
        let matchesFilter = filterType === 'all' || userType === filterType;
        if (filterType === 'paid') matchesFilter = user._isPaid === true;
        else if (filterType === 'free') matchesFilter = user._isPaid !== true;
        else if (filterType === 'subscribed') matchesFilter = user._hasRealSubscription === true;
        else if (filterType === 'promo') matchesFilter = user._isLaunchPromo === true;
        // 🔐 가입 승인제 (2026-08-25): 승인 대기 필터 — 필드 없음 = 승인됨(기존 회원)이라 pending만 잡힘
        else if (filterType === 'pendingApproval') matchesFilter = user.approvalStatus === 'pending';
        return matchesSearch && matchesFilter;
    }).sort((a, b) => {
        let aVal, bVal;
        if (sortBy === 'createdAt') {
            aVal = a.createdAt?.getTime() || 0;
            bVal = b.createdAt?.getTime() || 0;
        } else if (sortBy === 'email') {
            aVal = a.email || '';
            bVal = b.email || '';
        } else if (sortBy === 'name') {
            aVal = a.name || a.businessName || a.companyName || '';
            bVal = b.name || b.businessName || b.companyName || '';
        }
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
    });

    const stats = {
        total: users.length,
        dental: users.filter(u => getUserType(u) === 'dental').length,
        lab: users.filter(u => getUserType(u) === 'lab').length,
        seller: users.filter(u => getUserType(u) === 'seller').length,
        individual: users.filter(u => getUserType(u) === 'individual').length,
        paid: users.filter(u => u._isPaid === true).length,
        free: users.filter(u => u._isPaid !== true).length,
        subscribed: users.filter(u => u._hasRealSubscription === true).length,
        promo: users.filter(u => u._isLaunchPromo === true).length,
        pendingApproval: users.filter(u => u.approvalStatus === 'pending').length,
    };

    // ── S5: 외주 자격 메타 읽기(관리자 read 허용 firestore.rules vendorDocs) ──
    const isVendorType = (user) => ['lab', 'outsource'].includes(getUserType(user));
    const loadVendorMeta = useCallback(async (uid) => {
        setVendorMeta(prev => (prev[uid]?.docs ? prev : { ...prev, [uid]: { docs: [], loading: true, error: '' } }));
        try {
            const docs = await listVendorDocs(uid);
            setVendorMeta(prev => ({ ...prev, [uid]: { docs, loading: false, error: '' } }));
        } catch (e) {
            console.error('[회원관리] 자격 메타 읽기 실패 code=%s', e && e.code);
            setVendorMeta(prev => ({ ...prev, [uid]: { docs: [], loading: false, error: '번호 기록을 불러오지 못했습니다' } }));
        }
    }, []);
    const metaOf = (uid) => vendorMeta[uid] || { docs: [], loading: false, error: '' };
    const licenseDocs = (uid) => metaOf(uid).docs.filter(d => d.type === 'license');
    const numberDocs = (uid) => metaOf(uid).docs.filter(d => (d.type === 'opening' || d.type === 'mfg_permit') && typeof d.number === 'string');
    const hasGmpDoc = (uid) => metaOf(uid).docs.some(d => d.type === 'gmp' && d.path);
    // 외주 업체로 함께 등록할 수 있는 회원 = 제조사(outsource) 또는 외주 의향(wants) 기공소 — 서버 검증 4와 같은 조건
    const vendorEligible = (user) => getUserType(user) === 'outsource' || user.vendorIntent?.wants === true;
    const intentKinds = (user) => (getUserType(user) === 'outsource' ? ['cnc'] : (Array.isArray(user.vendorIntent?.kinds) ? user.vendorIntent.kinds.filter(k => VENDOR_KINDS.includes(k)) : []));
    // ── 외주 등록 보정(vendorOnly) 대상 = 이미 승인(approvalStatus 'approved' 명시 — 필드 없음은 서버가 pending 취급) + 대표 계정 + 외주 의향. 서버 decideTransition·planVendor 와 같은 조건 ──
    const vendorFixEligible = (user) => user.approvalStatus === 'approved' && !user.companyId && vendorEligible(user);
    const loadVendorProf = useCallback(async (uid, force = false) => {
        if (!force && vendorProf[uid] && !vendorProf[uid].error) return;
        setVendorProf(prev => ({ ...prev, [uid]: { prof: prev[uid]?.prof || null, loading: true, error: '' } }));
        try {
            const prof = await getVendorProfile(uid);
            setVendorProf(prev => ({ ...prev, [uid]: { prof, loading: false, error: '' } }));
        } catch (e) {
            console.error('[회원관리] 외주 프로필 읽기 실패 code=%s', e && e.code);
            setVendorProf(prev => ({ ...prev, [uid]: { prof: null, loading: false, error: '외주 프로필을 불러오지 못했습니다' } }));
        }
    }, [vendorProf]);
    const profOf = (uid) => vendorProf[uid] || { prof: null, loading: false, error: '' };
    // 한 줄 상태 문구: 없음 / 검토 중 / 승인됨 / 반려 / 만료 / 종료
    const vendorProfText = (uid) => {
        const p = profOf(uid);
        if (p.loading) return '외주 프로필 확인 중…';
        if (p.error) return p.error;
        if (!p.prof) return '외주 프로필 없음(vendorProfiles 미생성)';
        const st = p.prof.status || '';
        return `외주 프로필 ${VENDOR_STATUS_TEXT[st] || st || '상태 없음'}${Array.isArray(p.prof.kinds) && p.prof.kinds.length ? ` · ${p.prof.kinds.map(k => MF_KIND_LABEL[k] || k).join(', ')}` : ''}`;
    };
    const vendorProfApproved = (uid) => profOf(uid).prof?.status === 'approved';

    // 「식약처 안심책방에서 조회」 — 새 탭 + 번호 클립보드 복사(URL 에는 붙이지 않음). 복사 실패는 화면에만 표시(번호는 콘솔에 남기지 않는다)
    const openMfdsLookup = async (number) => {
        window.open(MFDS_LOOKUP_URL, '_blank', 'noopener');
        if (!number) return;
        try {
            await navigator.clipboard.writeText(String(number));
            setCopyMsg('번호를 복사했습니다 — 안심책방 검색창에 붙여 넣으세요');
        } catch (e) {
            console.error('[회원관리] 클립보드 복사 실패 name=%s', e && e.name);
            setCopyMsg('클립보드 복사에 실패했습니다 — 화면의 번호를 직접 입력하세요');
        }
        setTimeout(() => setCopyMsg(''), 4000);
    };

    // 🔐 가입 승인제 (2026-08-25 대표 확정) → S5(10-27): 서버 callable adminApproveSignup 1회(users 승인 + vendorProfiles + 알림). 클라이언트 updateDoc·알림 제거
    //   mode 'vendorOnly'(10-27 보정 진입점): 이미 approved 회원 → decision 'approved' + vendor.grant true 만 보내면 서버가 users 미변경·vendorProfiles 생성/보류 해제(signup-approval.js decideTransition)
    const setApproval = (user, status, mode = 'decide') => {
        const eligible = status === 'approved' && vendorEligible(user);
        if (eligible || isVendorType(user)) loadVendorMeta(user.id);
        if (mode === 'vendorOnly') loadVendorProf(user.id, true);
        const kinds = intentKinds(user);
        setApv({
            grant: eligible, kinds, hold: false, holdReason: HOLD_REASON_DEFAULT, gmpDate: '', cncApproved: false,
            rejectChoice: REJECT_CHOICES[0], rejectText: '',
        });
        setApvMsg('');
        setApprovalModal({ user, decision: status, mode });
    };
    const closeApproval = () => { setApprovalModal(null); setApv(null); setApvMsg(''); };
    const submitApproval = async () => {
        if (!approvalModal || !apv) return;
        const { user, decision, mode } = approvalModal;
        const vendorOnly = mode === 'vendorOnly';
        const payload = { uid: user.id, decision };
        if (vendorOnly && !apv.grant) { setApvMsg('외주 등록 보정은 「외주 업체로 등록」이 켜져 있어야 합니다'); return; }
        if (decision === 'rejected') {
            const reason = (apv.rejectChoice === '__etc' ? apv.rejectText : apv.rejectChoice).trim().slice(0, 300);
            if (!reason) { setApvMsg('거절 사유를 고르거나 적어 주세요'); return; }
            payload.rejectReason = reason;
        } else if (vendorEligible(user) && apv.grant) {
            const kinds = getUserType(user) === 'outsource' ? ['cnc'] : apv.kinds;
            if (!kinds.length) { setApvMsg('외주로 받을 종류를 1개 이상 고르세요(또는 「외주 업체로 함께 등록」을 끄세요)'); return; }
            const hasCnc = kinds.includes('cnc');
            if (hasCnc && !apv.hold && !apv.gmpDate) { setApvMsg('CNC 를 받는 업체는 GMP 만료일(업체 제출값)을 넣어 주세요. 서류가 없으면 「외주 보류」로 승인하세요'); return; }
            // 서버 계약: gmpExpireAt 은 ISO 문자열(Date 객체 금지), 오늘 이후만
            if (hasCnc && apv.gmpDate && apv.gmpDate < new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10)) { setApvMsg('GMP 만료일은 오늘 이후 날짜만 넣을 수 있습니다(이미 만료된 서류면 「외주 보류」로 승인하세요)'); return; }
            if (apv.hold && !apv.holdReason.trim()) { setApvMsg('보류 사유를 적어 주세요'); return; }
            payload.vendor = { grant: true, kinds, hold: !!apv.hold, gmpExpireAt: hasCnc && apv.gmpDate ? new Date(`${apv.gmpDate}T23:59:59+09:00`).toISOString() : null };
            if (apv.hold) payload.vendor.holdReason = apv.holdReason.trim().slice(0, 300);
            if (getUserType(user) === 'lab' && hasCnc) payload.vendor.cncApproved = !!apv.cncApproved;
        }
        try {
            setActionLoading(user.id);
            const res = await adminApproveSignup(payload);
            if (!vendorOnly) setUsers(prev => prev.map(u => u.id === user.id ? { ...u, approvalStatus: decision, approvalRejectReason: decision === 'approved' ? undefined : (payload.rejectReason || u.approvalRejectReason) } : u));
            closeApproval();
            // 서버 출력 { success, mode, vendor: null|{granted, hold, kinds, forced:['gmp'|'pledges']} } — forced 가 있으면 서버가 보류로 전환한 것(법무 L13·명세 §4-2 pledges 없음 → 보류)
            const rv = res && res.vendor;
            const forced = Array.isArray(rv?.forced) ? rv.forced : [];
            const forcedText = forced.length ? `\n\n서버가 보류로 전환했습니다(사유: ${forced.map(f => (f === 'gmp' ? 'GMP 서류 없음' : f === 'pledges' ? '서약 미확인' : f)).join(', ')}). ${forced.includes('pledges') ? '관리자가 「외주 업체 재심사」 화면에서 확인한 뒤 열립니다.' : '회원이 설정 › 외주 수주 설정에서 서류를 올리면 재심사로 올라옵니다.'}` : '';
            if (vendorOnly) {
                // 보정 성공 — users 는 바뀌지 않음(서버 vendorOnly). 프로필 상태·목록 새로고침
                alert(rv && rv.granted ? '외주 업체 등록을 보정했습니다(승인됨).' : `외주 업체 등록을 보정했습니다 — 보류(서류 확인 뒤 해제).${forcedText}`);
                setVendorProf(prev => { const n = { ...prev }; delete n[user.id]; return n; });
                await loadVendorProf(user.id, true);
                await loadUsers();
                return;
            }
            alert(decision === 'approved'
                ? (rv && rv.granted ? (rv.hold ? `가입을 승인했습니다. 외주 수주는 보류(서류 확인 뒤 해제)로 등록했습니다.${forcedText}` : '가입을 승인하고 외주 업체로 함께 등록했습니다.') : '가입을 승인했습니다.')
                : '가입을 거절 처리했습니다.');
        } catch (error) {
            // 서버 HttpsError 메시지를 그대로 보여 준다(번호 문자열은 서버가 넣지 않음 — 계약)
            console.error('[회원관리] 가입 승인 호출 실패 code=%s', error && error.code);
            setApvMsg(`처리하지 못했습니다: ${(error && error.message) || '알 수 없는 오류'}`);
        } finally {
            setActionLoading(null);
        }
    };

    const toggleUserStatus = async (user) => {
        try {
            setActionLoading(user.id);
            const newStatus = user.isActive === false ? true : false;
            await updateDoc(doc(db, 'users', user.id), {
                isActive: newStatus,
                updatedAt: serverTimestamp(),
            });
            setUsers(prev => prev.map(u => 
                u.id === user.id ? { ...u, isActive: newStatus } : u
            ));
            alert(newStatus ? '계정이 활성화되었습니다.' : '계정이 비활성화되었습니다.');
        } catch (error) {
            console.error('상태 변경 실패:', error);
            alert('상태 변경에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    const deleteUser = async () => {
        if (!selectedUser) return;
        try {
            setActionLoading(selectedUser.id);
            await deleteDoc(doc(db, 'users', selectedUser.id));
            setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
            setShowDeleteModal(false);
            setSelectedUser(null);
            alert('사용자가 삭제되었습니다.');
        } catch (error) {
            console.error('사용자 삭제 실패:', error);
            alert('사용자 삭제에 실패했습니다.');
        } finally {
            setActionLoading(null);
        }
    };

    const exportToCSV = () => {
        const headers = ['이메일', '이름/업체명', '유형', '결제구분', '실결제구독', '플랜', '구독만료일', '전화번호', '가입일', '상태'];
        const rows = filteredUsers.map(user => [
            user.email || '',
            user.name || user.businessName || user.companyName || '',
            getUserTypeLabel(getUserType(user)),
            user._isPaid ? (user._isLaunchPromo ? '유료(프로모션)' : (user._hasRealSubscription ? '유료(결제)' : '유료(특수)')) : '무료',
            user._hasRealSubscription ? 'O' : '',
            user._subscription?.plan || '',
            user._subscription?.expiresAt ? user._subscription.expiresAt.toLocaleDateString('ko-KR') : '',
            user.phone || '',
            user.createdAt ? user.createdAt.toLocaleDateString('ko-KR') : '',
            user.isActive === false ? '비활성' : '활성',
        ]);
        const csvContent = [headers, ...rows].map(row => 
            row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',')
        ).join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'users_' + new Date().toISOString().split('T')[0] + '.csv';
        link.click();
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const toggleRowExpand = (userId) => {
        const opening = !expandedRows[userId];
        setExpandedRows(prev => ({ ...prev, [userId]: !prev[userId] }));
        const u = users.find(x => x.id === userId);
        if (opening && u && isVendorType(u) && !vendorMeta[userId]) loadVendorMeta(userId);
        if (opening && u && vendorFixEligible(u)) loadVendorProf(userId);
    };

    const openCategoryModal = (user) => {
        setCategoryUser(user);
        setCategorySelections(user.outsourceCategories || []);
        setShowCategoryModal(true);
    };

    const toggleCategory = (catId) => {
        setCategorySelections(prev =>
            prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
        );
    };

    const saveCategories = async () => {
        if (!categoryUser) return;
        setSavingCategories(true);
        try {
            // 본인 문서에 저장
            await updateDoc(doc(db, 'users', categoryUser.id), {
                outsourceCategories: categorySelections,
                updatedAt: serverTimestamp(),
            });

            // companyId가 있고 본인 uid와 다르면 회사 문서에도 저장
            // (connections에서 companyId로 참조하기 때문)
            const companyId = categoryUser.companyId;
            if (companyId && companyId !== categoryUser.id) {
                try {
                    await updateDoc(doc(db, 'users', companyId), {
                        outsourceCategories: categorySelections,
                        updatedAt: serverTimestamp(),
                    });
                } catch (e) {
                    console.warn('회사 문서 카테고리 저장 스킵:', e);
                }
            }

            setUsers(prev => prev.map(u =>
                u.id === categoryUser.id ? { ...u, outsourceCategories: categorySelections } : u
            ));
            setShowCategoryModal(false);
            setCategoryUser(null);
            alert('외주 카테고리가 저장되었습니다.');
        } catch (error) {
            console.error('카테고리 저장 실패:', error);
            alert('카테고리 저장에 실패했습니다.');
        } finally {
            setSavingCategories(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} />
                <p>사용자 목록을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        <Users size={28} />
                        회원 관리
                    </h1>
                    <p style={styles.subtitle}>전체 회원을 관리합니다 (총 {users.length}명)</p>
                </div>
                <div style={styles.headerButtons}>
                    <button style={styles.exportBtn} onClick={exportToCSV}>
                        <Download size={18} />
                        CSV 다운로드
                    </button>
                    <button style={styles.refreshBtn} onClick={loadUsers}>
                        <RefreshCw size={18} />
                        새로고침
                    </button>
                </div>
            </div>

            {/* 통계 카드 */}
            <div style={styles.statsRow}>
                <div style={{...styles.statCard, ...(filterType === 'all' ? styles.statCardActive : {})}} onClick={() => setFilterType('all')}>
                    <Users size={24} color="#6366f1" />
                    <div>
                        <div style={styles.statValue}>{stats.total}</div>
                        <div style={styles.statLabel}>전체</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'dental' ? styles.statCardActive : {})}} onClick={() => setFilterType('dental')}>
                    <Building2 size={24} color="#10b981" />
                    <div>
                        <div style={styles.statValue}>{stats.dental}</div>
                        <div style={styles.statLabel}>치과</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'lab' ? styles.statCardActive : {})}} onClick={() => setFilterType('lab')}>
                    <Building2 size={24} color="#6366f1" />
                    <div>
                        <div style={styles.statValue}>{stats.lab}</div>
                        <div style={styles.statLabel}>기공소</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'seller' ? styles.statCardActive : {})}} onClick={() => setFilterType('seller')}>
                    <ShoppingBag size={24} color="#3b82f6" />
                    <div>
                        <div style={styles.statValue}>{stats.seller}</div>
                        <div style={styles.statLabel}>판매자</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'individual' ? styles.statCardActive : {})}} onClick={() => setFilterType('individual')}>
                    <User size={24} color="#64748b" />
                    <div>
                        <div style={styles.statValue}>{stats.individual}</div>
                        <div style={styles.statLabel}>개인</div>
                    </div>
                </div>
            </div>

            {/* 결제·구독 통계 */}
            <div style={styles.statsRow}>
                <div style={{...styles.statCard, ...(filterType === 'pendingApproval' ? styles.statCardActive : {}), ...(stats.pendingApproval > 0 ? { border: '2px solid #f59e0b' } : {})}} onClick={() => setFilterType('pendingApproval')}>
                    <Clock size={24} color="#d97706" />
                    <div>
                        <div style={{...styles.statValue, color: '#d97706'}}>{stats.pendingApproval}</div>
                        <div style={styles.statLabel}>승인 대기</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'paid' ? styles.statCardActive : {})}} onClick={() => setFilterType('paid')}>
                    <Crown size={24} color="#f59e0b" />
                    <div>
                        <div style={{...styles.statValue, color: '#f59e0b'}}>{stats.paid}</div>
                        <div style={styles.statLabel}>유료 회원</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'free' ? styles.statCardActive : {})}} onClick={() => setFilterType('free')}>
                    <User size={24} color="#94a3b8" />
                    <div>
                        <div style={{...styles.statValue, color: '#94a3b8'}}>{stats.free}</div>
                        <div style={styles.statLabel}>무료 회원</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'subscribed' ? styles.statCardActive : {})}} onClick={() => setFilterType('subscribed')}>
                    <CreditCard size={24} color="#10b981" />
                    <div>
                        <div style={{...styles.statValue, color: '#10b981'}}>{stats.subscribed}</div>
                        <div style={styles.statLabel}>실결제 구독중</div>
                    </div>
                </div>
                <div style={{...styles.statCard, ...(filterType === 'promo' ? styles.statCardActive : {})}} onClick={() => setFilterType('promo')}>
                    <Gift size={24} color="#8b5cf6" />
                    <div>
                        <div style={{...styles.statValue, color: '#8b5cf6'}}>{stats.promo}</div>
                        <div style={styles.statLabel}>런칭 프로모션</div>
                    </div>
                </div>
            </div>

            {/* 회원가입 유입 통계 */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, padding: '14px 18px', background: '#f0f0ff', borderRadius: 12, border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Users size={18} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: '#6366f1' }}>{signupStats.total}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>회원가입 페이지 총 유입</div>
                    </div>
                </div>
                <div style={{ flex: 1, padding: '14px 18px', background: '#ecfdf5', borderRadius: 12, border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Calendar size={18} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: '#10b981' }}>{signupStats.week}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>최근 7일 유입</div>
                    </div>
                </div>
                <div style={{ flex: 1, padding: '14px 18px', background: '#fffbeb', borderRadius: 12, border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckCircle size={18} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: '#f59e0b' }}>{signupStats.today}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>오늘 유입</div>
                    </div>
                </div>
            </div>

            {/* 검색 및 필터 */}
            <div style={styles.filterSection}>
                <div style={styles.searchBox}>
                    <Search size={20} color="#64748b" />
                    <input
                        type="text"
                        placeholder="이메일, 이름, 전화번호로 검색..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={styles.searchInput}
                    />
                </div>
                <div style={styles.filterGroup}>
                    <Filter size={18} color="#64748b" />
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <optgroup label="유형">
                            <option value="all">전체</option>
                            <option value="dental">치과</option>
                            <option value="lab">기공소</option>
                            <option value="outsource">외주업체</option>
                            <option value="seller">판매자</option>
                            <option value="individual">개인</option>
                            <option value="marketer">마케터</option>
                            <option value="admin">관리자</option>
                            <option value="staff">직원</option>
                        </optgroup>
                        <optgroup label="가입 승인">
                            <option value="pendingApproval">승인 대기</option>
                        </optgroup>
                        <optgroup label="결제·구독">
                            <option value="paid">유료 회원</option>
                            <option value="free">무료 회원</option>
                            <option value="subscribed">실결제 구독중</option>
                            <option value="promo">런칭 프로모션</option>
                        </optgroup>
                    </select>
                </div>
            </div>

            <div style={styles.resultInfo}>검색 결과: {filteredUsers.length}명</div>

            {/* 테이블 */}
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th} onClick={() => toggleSort('email')}>
                                <div style={styles.thContent}>
                                    이메일
                                    {sortBy === 'email' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                </div>
                            </th>
                            <th style={styles.th} onClick={() => toggleSort('name')}>
                                <div style={styles.thContent}>
                                    이름/업체명
                                    {sortBy === 'name' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                </div>
                            </th>
                            <th style={styles.th}>유형</th>
                            <th style={styles.th}>구독</th>
                            <th style={styles.th}>전화번호</th>
                            <th style={styles.th} onClick={() => toggleSort('createdAt')}>
                                <div style={styles.thContent}>
                                    가입일
                                    {sortBy === 'createdAt' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                </div>
                            </th>
                            <th style={styles.th}>상태</th>
                            <th style={styles.th}>관리</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={styles.emptyState}>
                                    <Users size={48} color="#cbd5e1" />
                                    <p>사용자가 없습니다</p>
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map(user => {
                                const userType = getUserType(user);
                                const typeColor = getUserTypeColor(userType);
                                return (
                                    <React.Fragment key={user.id}>
                                        <tr style={styles.tr} onClick={() => toggleRowExpand(user.id)}>
                                            <td style={styles.td}>
                                                <div style={styles.emailCell}>
                                                    <Mail size={14} color="#64748b" />
                                                    <span style={styles.emailText}>{user.email || '-'}</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                {user.name || user.businessName || user.companyName || '-'}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        ...styles.badge,
                                                        backgroundColor: typeColor.bg,
                                                        color: typeColor.text
                                                    }}>
                                                        {getUserTypeLabel(userType)}
                                                    </span>
                                                    {(userType === 'lab' || userType === 'outsource') && user.outsourceCategories && user.outsourceCategories.length > 0 && (
                                                        user.outsourceCategories.map(cat => (
                                                            <span key={cat} style={styles.categoryBadge}>
                                                                {getCategoryLabel(cat)}
                                                            </span>
                                                        ))
                                                    )}
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                                    {user._isPaid ? (
                                                        <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#92400e', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                            <Crown size={11} /> 유료
                                                        </span>
                                                    ) : (
                                                        <span style={{ ...styles.badge, backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                                            무료
                                                        </span>
                                                    )}
                                                    {user._hasRealSubscription && (
                                                        <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={`만료: ${user._subscription?.expiresAt ? user._subscription.expiresAt.toLocaleDateString('ko-KR') : '-'}`}>
                                                            <CreditCard size={11} /> 결제중
                                                        </span>
                                                    )}
                                                    {user._isLaunchPromo && (
                                                        <span style={{ ...styles.badge, backgroundColor: '#ede9fe', color: '#6d28d9', display: 'inline-flex', alignItems: 'center', gap: 4 }} title="2026-04-10 이전 가입자 자동 유료 전환">
                                                            <Gift size={11} /> 프로모션
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                {user.phone ? (
                                                    <div style={styles.phoneCell}>
                                                        <Phone size={14} color="#64748b" />
                                                        {user.phone}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td style={styles.td}>
                                                {(() => {
                                                    const d = user.createdAt;
                                                    if (!d || isNaN(d.getTime())) return '-';
                                                    return (
                                                        <div style={styles.dateCell}>
                                                            <Calendar size={14} color="#64748b" />
                                                            {`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                                    {user.isActive === false ? (
                                                        <span style={styles.statusInactive}>비활성</span>
                                                    ) : (
                                                        <span style={styles.statusActive}>활성</span>
                                                    )}
                                                    {/* 🔐 가입 승인제: 필드 없음 = 승인됨(기존 회원)이라 배지 생략 */}
                                                    {user.approvalStatus === 'pending' && (
                                                        <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                            <Clock size={11} /> 승인 대기
                                                        </span>
                                                    )}
                                                    {user.approvalStatus === 'rejected' && (
                                                        <span style={{ ...styles.badge, backgroundColor: '#fee2e2', color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                            <UserX size={11} /> 가입 거절
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                                                <div style={styles.actionButtons}>
                                                    {/* 🔐 가입 승인/거절 (pending → 둘 다, rejected → 승인으로 복구 가능) */}
                                                    {(user.approvalStatus === 'pending' || user.approvalStatus === 'rejected') && (
                                                        <button
                                                            style={{ ...styles.actionBtn, backgroundColor: '#d1fae5', color: '#059669' }}
                                                            onClick={() => setApproval(user, 'approved')}
                                                            disabled={actionLoading === user.id}
                                                            title="가입 승인"
                                                        >
                                                            <UserCheck size={16} />
                                                        </button>
                                                    )}
                                                    {user.approvalStatus === 'pending' && (
                                                        <button
                                                            style={{ ...styles.actionBtn, backgroundColor: '#fee2e2', color: '#dc2626' }}
                                                            onClick={() => setApproval(user, 'rejected')}
                                                            disabled={actionLoading === user.id}
                                                            title="가입 거절"
                                                        >
                                                            <UserX size={16} />
                                                        </button>
                                                    )}
                                                    {(userType === 'lab' || userType === 'outsource') && (
                                                        <button
                                                            style={{
                                                                ...styles.actionBtn,
                                                                backgroundColor: '#eef2ff',
                                                                color: '#6366f1'
                                                            }}
                                                            onClick={() => openCategoryModal(user)}
                                                            title="외주 카테고리 설정"
                                                        >
                                                            <Tag size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        style={{
                                                            ...styles.actionBtn,
                                                            backgroundColor: user.isActive === false ? '#d1fae5' : '#fee2e2',
                                                            color: user.isActive === false ? '#059669' : '#dc2626'
                                                        }}
                                                        onClick={() => toggleUserStatus(user)}
                                                        disabled={actionLoading === user.id}
                                                        title={user.isActive === false ? '활성화' : '비활성화'}
                                                    >
                                                        {actionLoading === user.id ? (
                                                            <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                                        ) : user.isActive === false ? (
                                                            <CheckCircle size={16} />
                                                        ) : (
                                                            <Ban size={16} />
                                                        )}
                                                    </button>
                                                    <button
                                                        style={{
                                                            ...styles.actionBtn,
                                                            backgroundColor: '#fee2e2',
                                                            color: '#dc2626'
                                                        }}
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        title="삭제"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRows[user.id] && (
                                            <tr style={styles.expandedRow}>
                                                <td colSpan="8" style={styles.expandedContent}>
                                                    <div style={styles.detailGrid}>
                                                        <div><strong>UID:</strong> {user.id}</div>
                                                        <div><strong>사업자번호:</strong> {user.businessNumber || '-'}</div>
                                                        <div><strong>주소:</strong> {user.address || user.storeAddress || '-'}</div>
                                                        <div><strong>회사ID:</strong> {user.companyId || '-'}</div>
                                                        <div><strong>역할:</strong> {user.role || '-'}</div>
                                                        <div><strong>판매자상태:</strong> {user.sellerStatus || '-'}</div>
                                                        <div><strong>가입승인:</strong> {user.approvalStatus === 'pending' ? '승인 대기' : user.approvalStatus === 'rejected' ? '거절' : user.approvalStatus === 'approved' ? '승인' : '승인(기존 회원)'}{user.approvalStatus === 'rejected' && user.approvalRejectReason ? ` — ${user.approvalRejectReason}` : ''}</div>
                                                    </div>
                                                    {/* S5(10-27): 외주 자격·번호·의향·서약 — 기공소·제조사만. 번호는 vendorDocs 메타(본인·관리자만 읽음) */}
                                                    {isVendorType(user) && (() => {
                                                        const m = metaOf(user.id);
                                                        const nums = numberDocs(user.id);
                                                        const lic = licenseDocs(user.id);
                                                        const vi = user.vendorIntent || null;
                                                        const vq = user.vendorQualification || null;
                                                        const pl = user.consents?.vendorPledges || null;
                                                        // 시행일(11-03) 전 가입·기존 pending 제조사는 번호가 users.vendorQualification.number 에만 있음(메타 없음) → 화면·클립보드에만 쓴다(users 수정·콘솔·URL 금지)
                                                        const legacyNo = !nums.length && typeof vq?.number === 'string' && vq.number ? vq.number : '';
                                                        const lookupNo = nums.length ? nums[nums.length - 1].number : legacyNo;
                                                        return (
                                                            <div style={{ marginTop: 12, padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                    <Building2 size={14} color="#6366f1" /> 외주 수주 자격 (번호는 관리자·본인만 열람)
                                                                    {vi?.metaFailed === true && <span style={{ ...styles.badge, backgroundColor: '#fee2e2', color: '#dc2626' }}>번호 기록 실패</span>}
                                                                    {vi?.licenseMissing === true && <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#b45309' }}>면허번호 미입력</span>}
                                                                    <span style={{ flex: 1 }} />
                                                                    {/* 10-27 「나중에 볼 것 ①」: 이미 승인된 외주 의향 회원의 외주 등록 보정 — 기존 승인 모달을 vendorOnly 모드로 재사용 */}
                                                                    {vendorFixEligible(user) && (
                                                                        vendorProfApproved(user.id)
                                                                            ? <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#059669' }} title="vendorProfiles 상태 approved">외주 이미 등록됨</span>
                                                                            : <button type="button" style={{ ...styles.actionBtn, backgroundColor: '#ecfdf5', color: '#047857', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600 }} onClick={() => setApproval(user, 'approved', 'vendorOnly')} disabled={actionLoading === user.id || profOf(user.id).loading} title="가입은 그대로 두고 외주 업체 프로필만 생성·보류 해제">
                                                                                <Building2 size={13} /> 외주 등록 보정
                                                                            </button>
                                                                    )}
                                                                    <button type="button" style={{ ...styles.actionBtn, backgroundColor: '#eef2ff', color: '#4338ca', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600 }} onClick={() => openMfdsLookup(lookupNo)} title="식약처 안심책방 새 탭 + 번호 복사">
                                                                        <ExternalLink size={13} /> 식약처 안심책방에서 조회
                                                                    </button>
                                                                </div>
                                                                {vendorFixEligible(user) && <div style={{ fontSize: 12.5, color: profOf(user.id).error ? '#dc2626' : '#475569', marginBottom: 6 }}>{vendorProfText(user.id)}</div>}
                                                                {copyMsg && <div style={{ fontSize: 12, color: '#4338ca', marginBottom: 6 }}>{copyMsg}</div>}
                                                                {m.loading && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>번호 기록을 불러오는 중…</div>}
                                                                {m.error && <div style={{ fontSize: 12.5, color: '#dc2626' }}>{m.error}</div>}
                                                                <div style={styles.detailGrid}>
                                                                    <div><strong>자격 종류:</strong> {vq?.type ? (QUAL_TYPE_LABEL[vq.type] || vq.type) : (userType === 'lab' ? '치과기공소 개설등록' : '-')}{vq?.premill ? ' · 프리밀드 사용' : ''}{vq?.numberProvided === true && !nums.length && !m.loading ? ' · 번호 제출됨(메타 없음)' : ''}</div>
                                                                    <div><strong>허가·개설등록번호:</strong> {nums.length ? nums.map(d => `${d.type === 'mfg_permit' ? '제조업 허가' : '개설등록'} ${d.number}`).join(' / ') : legacyNo ? `${vq.type === 'dental_lab' ? '개설등록' : '제조업 허가'} ${legacyNo} (가입 기록 · 메타 없음)` : (m.loading ? '…' : '-')}</div>
                                                                    <div><strong>치과기공사 면허:</strong> {lic.length ? lic.map(d => `${d.licenseNo || '-'} · ${d.licenseName || '-'}`).join(' / ') : (m.loading ? '…' : '-')}</div>
                                                                    <div><strong>외주 의향:</strong> {userType === 'outsource' ? 'CNC 제조업체(cnc)' : vi ? (vi.wants === true ? `받음 · ${(vi.kinds || []).map(k => MF_KIND_LABEL[k] || k).join(', ') || '종류 없음'}` : '안 받음') : '기록 없음(앱 가입·기존 회원)'}</div>
                                                                    <div><strong>외주 수주 의무 확인:</strong> {pl ? `확인함${pl.pledgeVersion ? ` (${pl.pledgeVersion}` : ''}${pl.agreedAt ? `${pl.pledgeVersion ? ', ' : ' ('}${fmtTs(pl.agreedAt)}` : ''}${pl.pledgeVersion || pl.agreedAt ? ')' : ''}` : '기록 없음'}</div>
                                                                    <div><strong>GMP 서류:</strong> {hasGmpDoc(user.id) ? '제출됨(업체 제출값 — 진위 확인 아님)' : '없음'}</div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                    <div style={{ marginTop: 12, padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <CreditCard size={14} color="#6366f1" /> 결제·구독 정보
                                                        </div>
                                                        {user._hasRealSubscription ? (
                                                            <div style={styles.detailGrid}>
                                                                <div><strong>구독 상태:</strong> <span style={{ color: '#10b981', fontWeight: 700 }}>실결제 구독중</span></div>
                                                                <div><strong>플랜:</strong> {user._subscription?.plan || '-'}</div>
                                                                <div><strong>상태:</strong> {user._subscription?.status || '-'}</div>
                                                                <div><strong>금액:</strong> {user._subscription?.amount ? user._subscription.amount.toLocaleString() + '원' : '-'}</div>
                                                                <div><strong>결제 수단:</strong> {user._subscription?.billingMethod || '-'}</div>
                                                                <div><strong>시작일:</strong> {user._subscription?.startedAt ? user._subscription.startedAt.toLocaleDateString('ko-KR') : '-'}</div>
                                                                <div><strong>만료일:</strong> {user._subscription?.expiresAt ? user._subscription.expiresAt.toLocaleDateString('ko-KR') : '-'}</div>
                                                                {user.companyId && !user._subscription?.userId && (
                                                                    <div style={{ color: '#64748b' }}><em>※ 회사(대표) 구독으로 적용됨</em></div>
                                                                )}
                                                            </div>
                                                        ) : user._isLaunchPromo ? (
                                                            <div style={{ fontSize: 13, color: '#6d28d9' }}>
                                                                <strong>런칭 프로모션 적용:</strong> 2026-04-10 이전 가입자로 자동 유료 전환됨 (실결제 없음)
                                                            </div>
                                                        ) : user._isPaid ? (
                                                            <div style={{ fontSize: 13, color: '#92400e' }}>
                                                                <strong>관리자/특수 유료:</strong> 결제 정보 없음 (관리자 권한 또는 특수 부여)
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: 13, color: '#64748b' }}>
                                                                <strong>무료 회원</strong> — 결제·구독 이력 없음
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* S5 가입 승인·거절 모달 — 서버 callable adminApproveSignup 1회 */}
            {approvalModal && apv && (() => {
                const { user: tUser, decision, mode } = approvalModal;
                const vendorOnly = mode === 'vendorOnly';
                const tType = getUserType(tUser);
                const eligible = decision === 'approved' && vendorEligible(tUser);
                const alreadyVendor = vendorOnly && vendorProfApproved(tUser.id);
                const kindsAvail = intentKinds(tUser);
                const hasCnc = eligible && apv.grant && (tType === 'outsource' || apv.kinds.includes('cnc'));
                const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, color: '#334155', textAlign: 'left', padding: '6px 0' };
                const inputStyle = { width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, boxSizing: 'border-box' };
                return (
                    <div style={styles.modalOverlay} onClick={closeApproval}>
                        <div style={{ ...styles.modalContent, maxWidth: 520, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
                            <h3 style={{ ...styles.modalTitle, fontSize: 18 }}>{vendorOnly ? '외주 등록 보정' : decision === 'approved' ? '가입 승인' : '가입 거절'} — {tUser.businessName || tUser.name || tUser.email}</h3>
                            {vendorOnly && (
                                <div style={{ fontSize: 13, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', marginBottom: 10, lineHeight: 1.5 }}>
                                    <div>가입은 이미 승인된 회원입니다 — 가입 상태는 바꾸지 않고 외주 업체 프로필만 생성·보류 해제·누락 보정합니다.</div>
                                    <div style={{ fontWeight: 700, color: profOf(tUser.id).error ? '#dc2626' : alreadyVendor ? '#059669' : '#0f172a' }}>현재: {vendorProfText(tUser.id)}{alreadyVendor ? ' — 이미 등록됨' : ''}</div>
                                </div>
                            )}
                            {decision === 'approved' && !eligible && (
                                <p style={styles.modalText}>가입을 승인합니다. 승인 알림은 서버가 보냅니다.{isVendorType(tUser) ? ' 이 회원은 외주 수주 의향이 없어 외주 업체 등록은 하지 않습니다.' : ''}</p>
                            )}
                            {eligible && (
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                                    <label style={{ ...rowStyle, fontWeight: 700 }}>
                                        <input type="checkbox" checked={apv.grant} disabled={vendorOnly} onChange={(e) => setApv(a => ({ ...a, grant: e.target.checked }))} style={{ marginTop: 3 }} />
                                        <span>{vendorOnly ? '외주 업체로 등록(보정)' : '외주 업체로 함께 등록'} <span style={{ fontWeight: 400, color: '#64748b' }}>({tType === 'outsource' ? 'CNC 제조업체' : '기공소 · 가입 때 외주 의향 있음'})</span></span>
                                    </label>
                                    {apv.grant && tType === 'lab' && (
                                        <div style={{ paddingLeft: 24 }}>
                                            <div style={{ fontSize: 12.5, color: '#64748b', margin: '4px 0' }}>받을 종류 (가입 때 고른 것 안에서)</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {kindsAvail.map(k => (
                                                    <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, border: `1px solid ${apv.kinds.includes(k) ? '#6366f1' : '#e2e8f0'}`, borderRadius: 8, padding: '4px 8px', cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={apv.kinds.includes(k)} onChange={() => setApv(a => ({ ...a, kinds: a.kinds.includes(k) ? a.kinds.filter(x => x !== k) : [...a.kinds, k] }))} />{MF_KIND_LABEL[k] || k}
                                                    </label>
                                                ))}
                                                {kindsAvail.length === 0 && <span style={{ fontSize: 12.5, color: '#dc2626' }}>가입 때 고른 종류가 없습니다</span>}
                                            </div>
                                        </div>
                                    )}
                                    {hasCnc && (
                                        <div style={{ paddingLeft: 24, marginTop: 8 }}>
                                            {tType === 'lab' && (
                                                <label style={rowStyle}><input type="checkbox" checked={apv.cncApproved} onChange={(e) => setApv(a => ({ ...a, cncApproved: e.target.checked }))} style={{ marginTop: 3 }} /><span>제조 허가 확인함 <span style={{ color: '#64748b' }}>(기공소 CNC — 체크 없으면 CNC 만 막힌 채 다른 종류만 받습니다)</span></span></label>
                                            )}
                                            <label style={rowStyle}>
                                                <span style={{ whiteSpace: 'nowrap', marginTop: 6 }}>GMP 만료일(업체 제출값)</span>
                                                <input type="date" style={{ ...inputStyle, width: 'auto' }} value={apv.gmpDate} disabled={apv.hold} onChange={(e) => setApv(a => ({ ...a, gmpDate: e.target.value }))} />
                                            </label>
                                            <div style={{ fontSize: 12, color: hasGmpDoc(tUser.id) ? '#64748b' : '#b45309', lineHeight: 1.5 }}>
                                                {hasGmpDoc(tUser.id) ? 'GMP 적합 인정서 메타가 있습니다. 서류의 만료일을 그대로 적으세요.' : 'GMP 적합 인정서가 없습니다 — 서버가 CNC 수주를 보류로 처리합니다(「외주 보류」를 켜고 승인하세요). 회원은 설정 › 외주 수주 설정에서 서류를 올립니다.'}
                                            </div>
                                        </div>
                                    )}
                                    {apv.grant && (
                                        <div style={{ paddingLeft: 24, marginTop: 8 }}>
                                            <label style={rowStyle}><input type="checkbox" checked={apv.hold} onChange={(e) => setApv(a => ({ ...a, hold: e.target.checked }))} style={{ marginTop: 3 }} /><span>외주 보류 <span style={{ color: '#64748b' }}>(가입은 승인, 외주 수주만 서류 확인 뒤 열림)</span></span></label>
                                            {apv.hold && <textarea style={{ ...inputStyle, minHeight: 56 }} maxLength={300} value={apv.holdReason} onChange={(e) => setApv(a => ({ ...a, holdReason: e.target.value }))} placeholder={HOLD_REASON_DEFAULT} />}
                                        </div>
                                    )}
                                </div>
                            )}
                            {!vendorOnly && decision === 'rejected' && (
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                                    <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 4 }}>거절 사유 (필수 — 회원 화면에 그대로 표시됩니다. 다른 업체 상호·내부 판단·단정 표현은 적지 마세요)</div>
                                    {[...REJECT_CHOICES, '__etc'].map(c => (
                                        <label key={c} style={rowStyle}>
                                            <input type="radio" name="rejectChoice" checked={apv.rejectChoice === c} onChange={() => setApv(a => ({ ...a, rejectChoice: c }))} style={{ marginTop: 3 }} />
                                            <span>{c === '__etc' ? '기타(직접 입력)' : c}</span>
                                        </label>
                                    ))}
                                    {apv.rejectChoice === '__etc' && <textarea style={{ ...inputStyle, minHeight: 56 }} maxLength={300} value={apv.rejectText} onChange={(e) => setApv(a => ({ ...a, rejectText: e.target.value }))} placeholder="거절 사유 (300자 이내)" />}
                                </div>
                            )}
                            {apvMsg && <div style={{ ...styles.modalWarning, justifyContent: 'flex-start', backgroundColor: '#fee2e2', color: '#b91c1c' }}><AlertTriangle size={16} /><span>{apvMsg}</span></div>}
                            <div style={styles.modalButtons}>
                                <button style={styles.cancelBtn} onClick={closeApproval} disabled={actionLoading === tUser.id}><X size={18} />취소</button>
                                <button style={{ ...styles.deleteBtn, backgroundColor: decision === 'approved' ? '#059669' : '#dc2626', opacity: alreadyVendor ? 0.5 : 1 }} onClick={submitApproval} disabled={actionLoading === tUser.id || alreadyVendor || (vendorOnly && profOf(tUser.id).loading)} title={alreadyVendor ? '외주 프로필이 이미 approved 입니다' : undefined}>
                                    {actionLoading === tUser.id ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : (decision === 'approved' ? <UserCheck size={18} /> : <UserX size={18} />)}
                                    {vendorOnly ? (alreadyVendor ? '이미 등록됨' : '외주 등록 보정') : decision === 'approved' ? '승인' : '거절'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* 삭제 확인 모달 */}
            {showDeleteModal && selectedUser && (
                <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalIcon}>
                            <AlertTriangle size={48} color="#dc2626" />
                        </div>
                        <h3 style={styles.modalTitle}>사용자 삭제</h3>
                        <p style={styles.modalText}>
                            <strong>{selectedUser.email}</strong> 사용자를 삭제하시겠습니까?
                        </p>
                        <div style={styles.modalWarning}>
                            <AlertTriangle size={16} />
                            <span>이 작업은 되돌릴 수 없습니다.</span>
                        </div>
                        <div style={styles.modalButtons}>
                            <button style={styles.cancelBtn} onClick={() => setShowDeleteModal(false)}>
                                <X size={18} />
                                취소
                            </button>
                            <button
                                style={styles.deleteBtn}
                                onClick={deleteUser}
                                disabled={actionLoading === selectedUser.id}
                            >
                                {actionLoading === selectedUser.id ? (
                                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <Trash2 size={18} />
                                )}
                                삭제
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 외주 카테고리 모달 */}
            {showCategoryModal && categoryUser && (
                <div style={styles.modalOverlay} onClick={() => setShowCategoryModal(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalIcon}>
                            <Tag size={48} color="#6366f1" />
                        </div>
                        <h3 style={styles.modalTitle}>외주 카테고리 설정</h3>
                        <p style={styles.modalText}>
                            <strong>{categoryUser.businessName || categoryUser.name || categoryUser.email}</strong>
                        </p>
                        <div style={styles.categoryList}>
                            {OUTSOURCE_CATEGORIES.map(cat => (
                                <label key={cat.id} style={styles.categoryItem}>
                                    <input
                                        type="checkbox"
                                        checked={categorySelections.includes(cat.id)}
                                        onChange={() => toggleCategory(cat.id)}
                                        style={styles.categoryCheckbox}
                                    />
                                    <span style={styles.categoryLabel}>{cat.label}</span>
                                </label>
                            ))}
                        </div>
                        <div style={styles.modalButtons}>
                            <button style={styles.cancelBtn} onClick={() => setShowCategoryModal(false)}>
                                <X size={18} />
                                취소
                            </button>
                            <button
                                style={{ ...styles.deleteBtn, backgroundColor: '#6366f1' }}
                                onClick={saveCategories}
                                disabled={savingCategories}
                            >
                                {savingCategories ? (
                                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <Save size={18} />
                                )}
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        gap: '16px',
        color: '#64748b',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#64748b',
        margin: '8px 0 0 0',
    },
    headerButtons: {
        display: 'flex',
        gap: '12px',
    },
    refreshBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#f1f5f9',
        color: '#475569',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    exportBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    statsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
    },
    statCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: '2px solid transparent',
    },
    statCardActive: {
        borderColor: '#6366f1',
        backgroundColor: '#f5f3ff',
    },
    statValue: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b',
    },
    statLabel: {
        fontSize: '12px',
        color: '#64748b',
    },
    filterSection: {
        display: 'flex',
        gap: '16px',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    searchBox: {
        flex: 1,
        minWidth: '300px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
    },
    searchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        fontSize: '14px',
    },
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
    },
    filterSelect: {
        border: 'none',
        outline: 'none',
        fontSize: '14px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
    },
    resultInfo: {
        fontSize: '13px',
        color: '#64748b',
        marginBottom: '12px',
    },
    tableContainer: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        minWidth: '900px',
    },
    th: {
        padding: '14px 16px',
        textAlign: 'left',
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    thContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    tr: {
        borderBottom: '1px solid #f1f5f9',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    td: {
        padding: '14px 16px',
        fontSize: '14px',
        color: '#1e293b',
        verticalAlign: 'middle',
    },
    emailCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    emailText: {
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    phoneCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    dateCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    badge: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
    },
    statusActive: {
        display: 'inline-block',
        padding: '4px 10px',
        backgroundColor: '#d1fae5',
        color: '#059669',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
    },
    statusInactive: {
        display: 'inline-block',
        padding: '4px 10px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
    },
    actionBtn: {
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
    },
    expandedRow: {
        backgroundColor: '#f8fafc',
    },
    expandedContent: {
        padding: '16px 24px',
    },
    detailGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '12px',
        fontSize: '13px',
        color: '#475569',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#94a3b8',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '400px',
        width: '90%',
        textAlign: 'center',
    },
    modalIcon: {
        marginBottom: '16px',
    },
    modalTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '12px',
    },
    modalText: {
        fontSize: '15px',
        color: '#64748b',
        marginBottom: '16px',
    },
    modalWarning: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#92400e',
        marginBottom: '24px',
    },
    modalButtons: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
    },
    cancelBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    deleteBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        backgroundColor: '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    categoryBadge: {
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: '600',
        backgroundColor: '#eef2ff',
        color: '#6366f1',
    },
    categoryList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        margin: '20px 0',
        textAlign: 'left',
    },
    categoryItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: '500',
        color: '#1e293b',
    },
    categoryCheckbox: {
        width: '18px',
        height: '18px',
        accentColor: '#6366f1',
        cursor: 'pointer',
    },
    categoryLabel: {
        flex: 1,
    },
};

export default AdminUserManagement;